import { FC } from "react";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { Notification } from "@/fetch/notifications";
import { ChevronIcon } from "@/components/Icon";
import { useNotificationAction, useTrackers, NotificationTypes } from "@/hooks";
import Typography from "@/components/Typography";
import { SocialAvatar } from "@/components/Social";
import Badge from "@/components/Badge";
import styles from "./MobileNotificationItem.module.scss";
import { usePublicProfileQuery } from "@/fetch/profiles/traveller";
import { useTravellerQuery } from "@/fetch/traveller";

type Props = {
  notification: Notification;
  onClick?: (notificationId: string) => void;
};

const MobileNotificationItem: FC<Props> = ({
  notification,
  onClick: propOnClick,
}) => {
  const { track } = useTrackers();
  const { translateNotification } = useNotificationAction();
  const router = useRouter();
  const { title, type, body, createdAt, status, _id, data } = notification;
  const {
    Icon,
    path,
    onClick,
    type: notificationType,
  } = translateNotification(notification);
  const travellerCid = data?.traveller_cid as number;
  const { data: publicProfile } = usePublicProfileQuery({ cid: travellerCid });
  const userProfile = publicProfile?.[0];
  const profilePicturePath = userProfile?.s3_profile_pic;
  const oneDaysAgo = dayjs().subtract(1, "day");
  const notificationDate = dayjs(createdAt);
  const isOlderThanOneDay = notificationDate.isBefore(oneDaysAgo);
  const date = notificationDate.fromNow(true);
  const isNotClickable = !onClick && !path;

  const onNotificationClick = async () => {
    if (isNotClickable) {
      return;
    }
    track("Notification Item Clicked", {
      eventId: "notification-item-clicked",
      trigger: "panel",
      intent: notification.intent,
    });
    onClick?.();
    path && router.push(path);
    if (_id) propOnClick?.(_id);
  };

  const hasSeen = status?.seen;

  return (
    <div
      className={styles.container}
      role="presentation"
      onClick={onNotificationClick}
    >
      {notificationType === NotificationTypes.OFFICIAL && Boolean(Icon) && (
        <Badge
          color="primary"
          invisible={Boolean(hasSeen)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          badgeContent=""
          classes={{ badge: styles.badge }}
        >
          <div className={styles.iconContainer}>
            <Icon variant="outlined" className={styles.officialIcon} />
          </div>
        </Badge>
      )}
      {notificationType === NotificationTypes.ACTIVITY && (
        <Badge
          color="primary"
          invisible={Boolean(hasSeen)}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          badgeContent=""
          classes={{ badge: styles.badge }}
        >
          <SocialAvatar
            cloudinaryPath={profilePicturePath}
            size="m"
            className={styles.avatar}
          />
        </Badge>
      )}
      <Typography variant="body1" className={styles.notificationTitle}>
        {title}
      </Typography>
      <div className={styles.rightContainer}>
        <Typography color="text.secondary" variant="body2">
          {isOlderThanOneDay ? notificationDate.format("MMM DD") : date}
        </Typography>

        <ChevronIcon />
      </div>
    </div>
  );
};

export default MobileNotificationItem;
